// pages/routing/RoutingChangeSet.jsx — SOUND-827 Phase 2
//
// /routing/:companyId/changes/:changeSetId — review one change set, tick the
// changes to apply, and GO. GO WRITES TO LIVE CHECKOUTCHAMP (there is no
// sandbox), so:
//   - GO / Decline / Roll back render only for internal admin|super
//     (canApproveRouting); the API enforces the same rule;
//   - GO is never enabled when the API's review says the set can't apply, when
//     a ticked change is locked (not verified / not allowlisted), or when the
//     ticked changes would leave a WEIGHTED profile off 100% (goButtonState);
//   - GO goes through a ConfirmDialog naming the client, and every action
//     button is disabled while any request is in flight, so a double click can
//     never send two applies.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, Lock, RotateCcw, XCircle, Play, RefreshCw } from "lucide-react";
import PageHeader from "components/PageLayout";
import RecordTable from "components/Records/RecordTable";
import ConfirmDialog from "components/ConfirmDialog";
import RelativeTime from "components/RelativeTime";
import Skeleton from "components/Skeleton";
import { useToast } from "components/Toast";
import { useAuth } from "utilities/auth";
import {
  useRoutingClients,
  useRoutingSnapshot,
  useRoutingChangeSet,
  useRoutingChangeSetActions,
} from "hooks/useRouting";
import { formatTimestamp } from "utilities/routingView";
import {
  canApproveRouting,
  changeLabel,
  formatChangeFromTo,
  formatFieldValue,
  changeStatusChip,
  changeStatusHelp,
  effectiveSetStatus,
  setStatusChip,
  changeCountsSummary,
  lockReasons,
  defaultTicked,
  goButtonState,
  canRollback,
  canDecline,
  sumTo100Problems,
  sumProblemText,
  warningText,
  applyRefusal,
} from "utilities/routingChanges";

function Banner({ tone = "warning", icon: Icon = AlertTriangle, children }) {
  const cls = {
    warning: "border-warning/30 bg-warning/10",
    destructive: "border-destructive/30 bg-destructive/10",
    info: "border-info/30 bg-info/10",
  }[tone];
  const iconCls = { warning: "text-warning", destructive: "text-destructive", info: "text-info" }[tone];
  return (
    <div className={`flex items-start gap-2 rounded-lg border px-4 py-2 text-sm text-foreground ${cls}`}>
      <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${iconCls}`} />
      <div className="min-w-0 space-y-1">{children}</div>
    </div>
  );
}

function sideEffectText(sideEffects) {
  if (!sideEffects || typeof sideEffects !== "object") return null;
  const parts = Object.entries(sideEffects).map(([field, v]) =>
    v && typeof v === "object" && ("from" in v || "to" in v)
      ? `${field} ${formatFieldValue(field, v.from)} → ${formatFieldValue(field, v.to)}`
      : `${field}: ${formatFieldValue(field, v)}`
  );
  return parts.length ? `Side effect: ${parts.join(", ")}` : null;
}

export default function RoutingChangeSet() {
  const { companyId, changeSetId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const auth = useAuth();
  const isAdmin = canApproveRouting(auth?.user);

  const { clients } = useRoutingClients();
  const client = clients.find((c) => c.companyId === companyId) || null;
  const clientName = client?.name || client?.slug || "this client";

  const { changeSet, review, loading, error, refetch } = useRoutingChangeSet(changeSetId);
  // Latest snapshot: labels for changes without one, and the client-side
  // sum-to-100 check over the TICKED changes (the API re-checks on a fresh one).
  const { snapshot } = useRoutingSnapshot(companyId);
  const { apply, decline, rollback, busy } = useRoutingChangeSetActions();

  const [ticked, setTicked] = useState([]);
  const initFor = useRef(null);
  useEffect(() => {
    if (!changeSet || !review || initFor.current === changeSet.changeSetId) return;
    initFor.current = changeSet.changeSetId;
    setTicked(defaultTicked(changeSet, review));
  }, [changeSet, review]);

  const [refusal, setRefusal] = useState(null);
  const [confirm, setConfirm] = useState(null); // "go" | "decline" | "rollback"
  const [declineNote, setDeclineNote] = useState("");

  const status = effectiveSetStatus(changeSet);
  const pending = status === "pending_approval";
  const changes = useMemo(
    () => [...(changeSet?.changes || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [changeSet]
  );
  const tickable = pending && isAdmin;
  // Apply order is the order shown to the approver.
  const tickedIds = changes.filter((c) => ticked.includes(c.changeId)).map((c) => c.changeId);
  const tickedChanges = changes.filter((c) => ticked.includes(c.changeId));
  const sumProblems = pending && snapshot ? sumTo100Problems(snapshot, tickedChanges) : [];
  const go = goButtonState({ changeSet, review, tickedIds, isAdmin, sumProblems });
  const showDecline = canDecline(changeSet, isAdmin);
  const showRollback = canRollback(changeSet, isAdmin);

  const toggle = (id) =>
    setTicked((t) => (t.includes(id) ? t.filter((x) => x !== id) : [...t, id]));
  const pendingIds = changes.filter((c) => (c.status || "pending") === "pending").map((c) => c.changeId);
  const allTicked = pendingIds.length > 0 && pendingIds.every((id) => ticked.includes(id));

  const handleGo = async () => {
    setConfirm(null);
    setRefusal(null);
    try {
      const body = await apply(changeSetId, tickedIds);
      const summary = changeCountsSummary(body.data);
      const st = body.data?.status;
      toast({
        status: st === "applied" ? "success" : "destructive",
        title: st === "applied" ? "Applied to CheckoutChamp" : setStatusChip(st).label,
        description: summary || undefined,
      });
    } catch (err) {
      if (err?.response?.status === 409) {
        setRefusal(applyRefusal(err));
        toast({ status: "destructive", title: "GO refused — nothing was written", description: err.response.data?.error });
      } else {
        toast({
          status: "destructive",
          title: "Apply failed",
          description:
            (err?.response?.data?.error || err?.message || "Unknown error") +
            " — reloading to show what CheckoutChamp actually holds.",
        });
      }
    } finally {
      refetch();
    }
  };

  const handleDecline = async () => {
    setConfirm(null);
    try {
      await decline(changeSetId, declineNote.trim());
      toast({ status: "success", title: "Change set declined" });
      setDeclineNote("");
    } catch (err) {
      toast({ status: "destructive", title: "Decline failed", description: err?.response?.data?.error || err?.message });
    } finally {
      refetch();
    }
  };

  const handleRollback = async () => {
    setConfirm(null);
    try {
      const body = await rollback(changeSetId);
      const id = body.data?.changeSetId;
      toast({
        status: "success",
        title: "Rollback proposed",
        description: "Nothing changes in CheckoutChamp until an admin ticks and GOes it.",
      });
      if (id) navigate(`/routing/${companyId}/changes/${id}`);
      else refetch();
    } catch (err) {
      toast({ status: "destructive", title: "Rollback failed", description: err?.response?.data?.error || err?.message });
    }
  };

  const columns = [
    tickable && {
      key: "tick",
      header: (
        <input
          type="checkbox"
          className="h-4 w-4 rounded border-border"
          aria-label="Tick all pending changes"
          checked={allTicked}
          disabled={!!busy}
          onChange={() => setTicked(allTicked ? [] : pendingIds)}
        />
      ),
      width: 44,
      truncate: false,
      cell: (c) =>
        (c.status || "pending") === "pending" ? (
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-border"
            aria-label={`Apply change ${c.order ?? ""}`}
            checked={ticked.includes(c.changeId)}
            disabled={!!busy}
            onChange={() => toggle(c.changeId)}
          />
        ) : null,
    },
    {
      key: "label",
      header: "Change",
      width: "30%",
      title: (c) => changeLabel(c, snapshot),
      cell: (c) => <span className="font-medium">{changeLabel(c, snapshot)}</span>,
    },
    {
      key: "fromTo",
      header: "From → to",
      width: "22%",
      truncate: false,
      cell: (c) => {
        const { from, to } = formatChangeFromTo(c);
        const se = sideEffectText(c.sideEffects);
        return (
          <div className="whitespace-normal">
            <span className="text-muted-foreground">{from}</span> → <span className="font-medium">{to}</span>
            {se && <div className="text-xs text-warning">{se}</div>}
          </div>
        );
      },
    },
    {
      key: "reason",
      header: "Reason",
      width: "18%",
      cell: (c) => <span className="text-muted-foreground">{c.reason || "—"}</span>,
    },
    {
      key: "status",
      header: "Status",
      width: "26%",
      truncate: false,
      cell: (c) => {
        const chip = changeStatusChip(c.status || "pending");
        const locks = pending ? lockReasons(review?.perChange?.[c.changeId]) : [];
        const refused = refusal?.byChange?.[c.changeId];
        const help = changeStatusHelp(c.status);
        return (
          <div className="space-y-1 whitespace-normal">
            <span className={`badge ${chip.badge}`} title={help || undefined}>
              {chip.label}
            </span>
            {locks.map((l) => (
              <div key={l} className="flex items-center gap-1 text-xs text-muted-foreground">
                <Lock className="h-3.5 w-3.5 shrink-0" />
                {l}
              </div>
            ))}
            {refused && <div className="text-xs text-destructive">GO refused: {refused}</div>}
            {c.result?.message && (
              <div className={`text-xs ${c.status === "failed" ? "text-destructive" : "text-muted-foreground"}`}>
                {c.status === "failed" ? "CheckoutChamp: " : ""}
                {typeof c.result.message === "string" ? c.result.message : JSON.stringify(c.result.message)}
              </div>
            )}
          </div>
        );
      },
    },
  ];

  const warnings = [
    ...(pending ? (review?.warnings || []).map(warningText).filter(Boolean) : []),
    ...sumProblems.map(sumProblemText),
  ];

  const statusChip = setStatusChip(status);
  const actions = (
    <>
      {showRollback && (
        <button type="button" className="btn btn-outline" disabled={!!busy} onClick={() => setConfirm("rollback")}>
          <RotateCcw className="h-4 w-4" />
          Roll back
        </button>
      )}
      {showDecline && (
        <button type="button" className="btn btn-ghost" disabled={!!busy} onClick={() => setConfirm("decline")}>
          <XCircle className="h-4 w-4" />
          Decline
        </button>
      )}
      {go.show && (
        <button
          type="button"
          className="btn btn-primary"
          disabled={!go.enabled || !!busy}
          title={go.reason || undefined}
          onClick={() => setConfirm("go")}
        >
          <Play className="h-4 w-4" />
          {busy === "apply" ? "Applying…" : go.label}
        </button>
      )}
      <button type="button" className="btn btn-ghost" onClick={() => navigate(`/routing/${companyId}?tab=changes`)}>
        <ArrowLeft className="h-4 w-4" />
        Back
      </button>
    </>
  );

  // useGet keeps the previous set's data while a new id loads (e.g. right after
  // Roll back navigates to the new set) — never render, tick or GO on it.
  const switching = !!changeSet?.changeSetId && changeSet.changeSetId !== changeSetId;
  if ((loading && !changeSet) || switching) {
    return (
      <div className="container space-y-3 py-6">
        <Skeleton.Rect height={60} width="100%" />
        <Skeleton.Rect height={240} width="100%" />
      </div>
    );
  }
  if (error || !changeSet) {
    return (
      <div className="container py-6">
        <div className="surface p-8 text-center">
          <p className="text-destructive">
            {error?.response?.status === 404
              ? "This change set does not exist."
              : error?.response?.data?.error || error?.message || "Failed to load the change set."}
          </p>
          <div className="mt-3 flex justify-center gap-2">
            <button className="btn btn-ghost" onClick={refetch}>
              Retry
            </button>
            <button className="btn btn-ghost" onClick={() => navigate(`/routing/${companyId}?tab=changes`)}>
              Back to changes
            </button>
          </div>
        </div>
      </div>
    );
  }

  const cs = changeSet;
  return (
    <div>
      <PageHeader
        title={cs.title || "Change set"}
        badge={<span className={`badge ${statusChip.badge}`}>{statusChip.label}</span>}
        subtitle={`${client?.name || client?.slug || "CheckoutChamp routing"} · proposed by ${
          cs.author?.email || cs.author?.service || (cs.source === "model" ? "the model" : "unknown")
        } · ${formatTimestamp(cs.createdAt)}`}
        actions={actions}
      />

      <div className="container space-y-4 py-6">
        {cs.note && <div className="surface p-4 text-sm whitespace-pre-wrap">{cs.note}</div>}

        <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground">
          <span>
            {snapshot && snapshot.snapshotId === cs.basedOnSnapshotId
              ? `Based on the latest snapshot (${formatTimestamp(snapshot.takenAt)})`
              : "Based on an earlier snapshot — GO re-reads CheckoutChamp first and marks anything that moved as stale"}
          </span>
          {pending && cs.expiresAt && (
            <span>
              Expires <RelativeTime value={cs.expiresAt} /> ({formatTimestamp(cs.expiresAt)})
            </span>
          )}
          {cs.approval?.approvedAt && (
            <span>
              Approved by {cs.approval.approvedBy?.email || "—"} · {formatTimestamp(cs.approval.approvedAt)}
            </span>
          )}
          {cs.apply?.finishedAt && <span>Applied {formatTimestamp(cs.apply.finishedAt)}</span>}
          {cs.rollbackOf && (
            <span>
              Rollback of{" "}
              <Link className="text-foreground hover:text-brand hover:underline" to={`/routing/${companyId}/changes/${cs.rollbackOf}`}>
                an earlier change set
              </Link>
            </span>
          )}
        </div>

        {status === "applying" && (
          <Banner tone="info" icon={RefreshCw}>
            <span>This change set is being applied right now.</span>
            <button className="btn btn-ghost h-7 px-2 text-xs" onClick={refetch}>
              Reload
            </button>
          </Banner>
        )}
        {status === "expired" && (
          <Banner tone="info">This change set expired — change sets must be applied within 7 days of being proposed. Propose it again on a fresh snapshot.</Banner>
        )}
        {cs.apply?.refusal && <Banner tone="destructive">GO was refused: {cs.apply.refusal}</Banner>}
        {refusal && (
          <Banner tone="destructive">
            <div className="font-medium">GO refused before anything was written: {refusal.message}</div>
            {refusal.general.map((g) => (
              <div key={g}>{g}</div>
            ))}
            {Object.keys(refusal.byChange).length > 0 && (
              <div>Untick the changes marked "GO refused" below and try again.</div>
            )}
          </Banner>
        )}
        {warnings.length > 0 && (
          <Banner>
            {warnings.map((w) => (
              <div key={w}>{w}</div>
            ))}
          </Banner>
        )}

        {pending && !isAdmin && go.waiting && (
          <Banner tone="info" icon={Lock}>
            {go.waiting}.
          </Banner>
        )}
        {go.show && !go.enabled && go.reason && (
          <div className="text-sm text-muted-foreground">GO is unavailable: {go.reason}.</div>
        )}

        <RecordTable
          columns={columns}
          rows={changes}
          rowKey={(c) => c.changeId}
          rowClassName={(c) =>
            tickable && (c.status || "pending") === "pending" && !ticked.includes(c.changeId) ? "opacity-60" : ""
          }
          empty={{ heading: "This change set has no changes" }}
        />
      </div>

      <ConfirmDialog
        open={confirm === "go"}
        onCancel={() => setConfirm(null)}
        onConfirm={handleGo}
        variant="destructive"
        size="md"
        title={go.label}
        description={`This changes live CheckoutChamp routing for ${clientName}. There is no sandbox. Changes apply in the order shown and stop at the first failure.`}
        confirmLabel={go.label}
        confirmDisabled={!go.enabled || !!busy}
      >
        <ol className="max-h-56 list-decimal space-y-1 overflow-y-auto pl-5 text-sm">
          {tickedChanges.map((c) => {
            const { from, to } = formatChangeFromTo(c);
            return (
              <li key={c.changeId}>
                <span className="font-medium">{changeLabel(c, snapshot)}</span>
                <span className="text-muted-foreground">
                  {" "}
                  {from} → {to}
                </span>
              </li>
            );
          })}
        </ol>
        {changes.length > tickedChanges.length && pending && (
          <p className="text-xs text-muted-foreground">
            {changes.filter((c) => (c.status || "pending") === "pending").length - tickedChanges.length} unticked
            change(s) will be declined.
          </p>
        )}
      </ConfirmDialog>

      <ConfirmDialog
        open={confirm === "decline"}
        onCancel={() => setConfirm(null)}
        onConfirm={handleDecline}
        title="Decline this change set?"
        description="Nothing is written to CheckoutChamp. The proposal is kept, marked declined."
        confirmLabel="Decline"
        size="md"
      >
        <textarea
          className="textarea min-h-[72px] w-full"
          placeholder="Note (optional)"
          value={declineNote}
          onChange={(e) => setDeclineNote(e.target.value)}
        />
      </ConfirmDialog>

      <ConfirmDialog
        open={confirm === "rollback"}
        onCancel={() => setConfirm(null)}
        onConfirm={handleRollback}
        title="Propose a rollback?"
        description="This creates a NEW change set that reverses this set's applied changes, newest first, from CheckoutChamp's current values. It goes through the same tick-and-GO — nothing changes until an admin applies it."
        confirmLabel="Create rollback"
        size="md"
      />
    </div>
  );
}
